/**
 * SealantTrackerCard — силанти на ями и фисури.
 * Състояние по зъб, интервал за проверка според риска, индикации и
 * контраиндикации по учебника.
 * Данни: GET /api/dental-prevention/sealants · POST /sealants/tooth
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { useAuth } from "@/App";

const STATUS_STYLE = {
  sealed: "bg-emerald-500/20 text-emerald-600 border-emerald-500/40",
  candidate: "bg-amber-500/20 text-amber-600 border-amber-500/40",
  lost: "bg-rose-500/20 text-rose-600 border-rose-500/40",
  restored: "bg-sky-500/20 text-sky-600 border-sky-500/40",
  missing: "bg-slate-500/20 opacity-60",
  not_needed: "bg-slate-500/10 opacity-70",
  unknown: "",
};
const GROUPS = [
  ["first_molar", "Първи постоянни молари (~6 г.)"],
  ["second_molar", "Втори постоянни молари (~12 г.)"],
  ["premolar", "Премолари"],
  ["primary", "Временни молари"],
];

export default function SealantTrackerCard({ isDark = false, cardCls = "", childId = null }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [info, setInfo] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dental-prevention/sealants`, {
        params: childId ? { child_id: childId } : {},
      });
      setD(data);
    } catch { setD(null); }
  }, [API, childId]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const border = isDark ? "border-white/10" : "border-gray-200";
  const inputCls = `px-2 py-1 rounded-lg border text-[12px] ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;

  const saveTooth = async (tooth, patch) => {
    try {
      const { data } = await axios.post(`${API}/dental-prevention/sealants/tooth`, {
        fdi: tooth.fdi,
        child_id: childId,
        status: patch.status ?? tooth.status,
        sealed_on: patch.sealed_on ?? tooth.sealed_on ?? null,
        deep_fissure: patch.deep_fissure ?? tooth.deep_fissure,
        note: patch.note ?? tooth.note,
      });
      setD(data);
    } catch { toast.error("Не се записа"); }
  };

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="dhi-sealants-card">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Shield className="w-4 h-4 text-cyan-500" /> Силанти на ями и фисури
          </div>
          <p className="text-[11px] opacity-70 mt-0.5">{d.intro_bg}</p>
        </div>
        <div className="flex gap-1.5 flex-wrap shrink-0">
          {[["sealed", "силанирани"], ["candidate", "за поставяне"],
            ["lost", "изпаднали"], ["review_due", "за проверка"]].map(([k, lbl]) => (
            <span key={k} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-600"
              data-testid={`sealants-count-${k}`}>
              {d.summary[k]} {lbl}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-2 space-y-1" data-testid="sealants-actions">
        {d.actions_bg.map((a, i) => (
          <div key={i} className="text-[11px] opacity-80 flex gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" /> {a}
          </div>
        ))}
      </div>

      {GROUPS.map(([g, label]) => (
        <div key={g} className="mt-3">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1">{label}</div>
          <div className="flex flex-wrap gap-1.5">
            {d.teeth.filter((t) => t.group === g).map((t) => (
              <button key={t.fdi} onClick={() => setOpen(open === t.fdi ? null : t.fdi)}
                className={`px-2.5 py-1.5 rounded-xl text-[11px] font-bold border transition ${
                  STATUS_STYLE[t.status] || border} ${t.overdue_days > 0 ? "ring-2 ring-amber-500/50" : ""}`}
                data-testid={`sealant-tooth-${t.fdi}`}>
                {t.fdi}
                <span className="block text-[9px] font-normal opacity-80">{t.status_bg}</span>
              </button>
            ))}
          </div>
          {d.teeth.filter((t) => t.group === g && t.fdi === open).map((t) => (
            <div key={t.fdi} className={`mt-2 p-3 rounded-xl border ${border} space-y-2`}
              data-testid={`sealant-editor-${t.fdi}`}>
              <div className="text-[11px] font-bold">{t.label_bg}</div>
              <div className="flex flex-wrap gap-1.5">
                {d.statuses.map((s) => (
                  <button key={s.key} onClick={() => saveTooth(t, { status: s.key })}
                    className={`px-2 py-1 rounded-full text-[10px] font-bold border ${
                      t.status === s.key ? "border-cyan-500 bg-cyan-500/15 text-cyan-500" : `${border} opacity-70`}`}
                    data-testid={`sealant-status-${t.fdi}-${s.key}`}>
                    {s.label_bg}
                  </button>
                ))}
              </div>
              {t.status === "sealed" && (
                <div className="flex flex-wrap gap-2 items-center">
                  <input type="date" value={t.sealed_on || ""}
                    onChange={(e) => saveTooth(t, { sealed_on: e.target.value })}
                    className={inputCls} data-testid={`sealant-date-${t.fdi}`} />
                  {t.review_due && (
                    <span className={`text-[10px] font-bold ${t.overdue_days > 0 ? "text-amber-500" : "opacity-70"}`}
                      data-testid={`sealant-review-${t.fdi}`}>
                      проверка: {t.review_due}
                      {t.overdue_days > 0 ? ` (закъснение ${t.overdue_days} дни)` : ""}
                    </span>
                  )}
                </div>
              )}
              <label className="flex items-center gap-2 text-[10px] opacity-80">
                <input type="checkbox" checked={!!t.deep_fissure}
                  onChange={(e) => saveTooth(t, { deep_fissure: e.target.checked })}
                  data-testid={`sealant-deep-${t.fdi}`} />
                Дълбока, неравна фисура (по-добро задържане на силанта)
              </label>
            </div>
          ))}
        </div>
      ))}

      <button onClick={() => setInfo(!info)}
        className="mt-3 text-[11px] font-bold text-cyan-500 inline-flex items-center gap-1"
        data-testid="sealants-info-toggle">
        {info ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        Как и защо се поставя силант
      </button>
      {info && (
        <div className="mt-2 space-y-2" data-testid="sealants-info">
          <div className="text-[10px] opacity-80">{d.definition_bg}</div>
          {[["Индикации", d.indications_bg], ["Контраиндикации", d.contraindications_bg],
            ["Задържане", d.retention_bg], ["Процедура", d.procedure_bg]].map(([title, list]) => (
            <div key={title}>
              <div className="text-[10px] uppercase font-bold opacity-55">{title}</div>
              <ul className="mt-0.5 space-y-0.5">
                {list.map((x, i) => <li key={i} className="text-[10px] opacity-75">· {x}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      <div className="text-[9px] opacity-50 mt-2">{d.note_bg}</div>
    </div>
  );
}
